/**
 * Identification Page - ID selection and entry step
 *
 * Screen for selecting ID type and entering ID information
 * Part of the sequential flow: Calculator → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, IdCard } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { FormReminder } from "@/components/forms/FormReminder";
import { RampsButton } from "@/components/ramps/Button";
import { getSubdivisions } from "@/constants/subdivisions";
import { isBankService } from "@/domain/calculator";
import { useContinue } from "@/hooks/useContinue";
import { useCreateProfile } from "@/hooks/useCreateProfile";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { toast } from "@/lib/toast.tsx";
import { normalizeIdType } from "@/utils/idRequirements";
import { RampsInput } from "../components/ramps/Input";
import { RampsSelectBottomSheet } from "../components/ramps/RampsSelectBottomSheet";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import { useCountries } from "../hooks/useCountries";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

// Function to determine default ID type based on address and payout countries
const getDefaultIdType = (
	addressCountry: string,
	payoutCountry: string,
): string => {
	// If both countries are not set, return default
	if (
		!addressCountry ||
		!payoutCountry ||
		addressCountry.trim() === "" ||
		payoutCountry.trim() === ""
	) {
		return "GOV";
	}

	// If both addressCountry and payoutCountry are USA or CAN, default to DRV
	const northAmericanCountries = ["USA", "CAN"];
	if (
		northAmericanCountries.includes(addressCountry) &&
		northAmericanCountries.includes(payoutCountry)
	) {
		return "DRV";
	}

	// If addressCountry and payoutCountry are different, default to PAS
	if (addressCountry !== payoutCountry) {
		return "PAS";
	}

	// Otherwise, default to GOV
	return "GOV";
};

// Countries that have state/subdivision selection; document types issued at state level
const COUNTRIES_WITH_STATES = ["USA", "CAN", "MEX"];
const STATE_ISSUED_DOCUMENTS = ["DRV", "STA"]; // Driver's License, State ID

// Function to get ID types based on destination country
const getIdTypesForCountry = (
	destinationCountry: string,
	countryOptions: Array<{ value: string; label: string }>,
) => {
	const country = countryOptions.find((c) => c.value === destinationCountry);
	const countryName = country?.label || destinationCountry;

	if (destinationCountry === "USA") {
		// USA: All ID types including Alien ID and State ID
		return [
			{ value: "GOV", label: t`Government ID` },
			{ value: "DRV", label: t`Driver's License` },
			{ value: "PAS", label: t`Passport` },
			{ value: "ALN", label: t`Alien ID` },
			{ value: "STA", label: t`State ID` },
		];
	} else if (destinationCountry === "CAN") {
		// Canada: All ID types except Alien ID
		return [
			{ value: "GOV", label: t`Government ID` },
			{ value: "DRV", label: t`Driver's License` },
			{ value: "PAS", label: t`Passport` },
			{ value: "STA", label: t`State ID` },
		];
	} else if (destinationCountry === "ARG") {
		return [
			{ value: "PAS", label: t`Passport` },
			{ value: "GOV", label: t`${countryName} Government ID` },
			{ value: "ALN", label: t`Alien ID/Precaria` },
		];
	} else {
		// Other countries: Only Passport and Government ID (prefixed with country name)
		return [
			{ value: "PAS", label: t`Passport` },
			{ value: "GOV", label: t`${countryName} Government ID` },
		];
	}
};

function IdentificationPage() {
	const search = Route.useSearch();
	const store = useRampsStore();
	const handleBack = useNavigateBack();
	const handleContinue = useContinue();
	const { trackPageView } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("identification", { page_name: "identification" });
	}, [trackPageView]);

	// Generate unique ID for form field
	const idNumberFieldId = useId();

	// Use countries hook for centralized data access
	const { countryOptions } = useCountries();

	// Get destination country from store (payout country)
	const destinationCountry = store.payoutCountry || "USA";

	// Get dynamic ID types based on destination country
	const ID_TYPES = getIdTypesForCountry(destinationCountry, countryOptions);

	// State variables
	const [idType, setIdType] = useState(normalizeIdType(store.idType));
	const [idNumber, setIdNumber] = useState(store.idNumber);
	const [issueCountry, setIssueCountry] = useState(store.idIssueCountry);
	const [issueState, setIssueState] = useState(store.idIssueState);
	// Track if user has manually selected idType (or if store already has a meaningful value)
	const [hasUserSelectedIdType, setHasUserSelectedIdType] = useState(
		!!store.idType && store.idType !== "state-id", // Don't consider default "state-id" as user selection
	);
	// Track if user has manually selected issueCountry on THIS page (not auto-populated)
	const [hasUserSelectedIssueCountry, setHasUserSelectedIssueCountry] =
		useState(false);

	// Token and transaction_id are synced globally by URLParamsInitializer
	// Initialize auth token with fallback logic (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;

	// Profile creation hook
	const {
		createProfileIfNeeded,
		isCreating,
		isAssociating,
		createError,
		associateError,
	} = useCreateProfile(authToken);

	// Handle profile creation errors
	useEffect(() => {
		if (createError) {
			toast.error(
				createError.message || t`Failed to create profile. Please try again.`,
			);
		}
	}, [createError]);

	// Handle wallet association errors
	useEffect(() => {
		if (associateError) {
			toast.error(
				associateError.message ||
					t`Failed to associate wallet. Please try again.`,
			);
		}
	}, [associateError]);

	// Function to determine if state selection should be shown
	const shouldShowIssueState = useCallback(
		(docType: string, country: string): boolean => {
			// Only show state for countries that have states
			if (!COUNTRIES_WITH_STATES.includes(country)) {
				return false;
			}

			// Only show state for document types that are issued at state level
			return STATE_ISSUED_DOCUMENTS.includes(docType);
		},
		[],
	);

	// Set initial issue country from payout country
	useEffect(() => {
		// Only set default if user hasn't manually selected an issue country
		if (hasUserSelectedIssueCountry) {
			return;
		}

		const hasCountries = countryOptions.length > 0;
		const hasPayoutCountry = !!store.payoutCountry;

		if (hasCountries && hasPayoutCountry) {
			// Default the ID issue country to the payout/destination country.
			// Do NOT fall back to the address country: where an ID was issued is
			// distinct from where the user lives, and coupling them silently
			// overwrites the ID issue country with the address country.
			const initialIssueCountry = store.idIssueCountry || destinationCountry;
			const countryExists = countryOptions.find(
				(c) => c.value === initialIssueCountry,
			);

			if (countryExists && issueCountry !== initialIssueCountry) {
				setIssueCountry(initialIssueCountry);
			}
		}
	}, [
		countryOptions,
		destinationCountry,
		store.payoutCountry,
		store.idIssueCountry,
		hasUserSelectedIssueCountry,
		issueCountry,
	]);

	// Clear issue state when switching to a country or document type that doesn't require states
	useEffect(() => {
		if (!shouldShowIssueState(idType, issueCountry)) {
			setIssueState("");
		}
	}, [idType, issueCountry, shouldShowIssueState]);

	// Set default idType based on address and payout countries
	useEffect(() => {
		// Only set default if user hasn't manually selected an idType
		if (hasUserSelectedIdType) {
			return;
		}

		// Wait for countries to be available and ID_TYPES to be loaded
		if (
			!store.addressCountry ||
			!store.payoutCountry ||
			ID_TYPES.length === 0
		) {
			return;
		}

		// Calculate the default idType
		const defaultIdType = getDefaultIdType(
			store.addressCountry,
			store.payoutCountry,
		);

		// Check if the default idType is available in the current ID_TYPES options
		const isDefaultAvailable = ID_TYPES.some(
			(type) => type.value === defaultIdType,
		);

		// If the calculated default is not available, try fallback options
		let finalIdType = defaultIdType;
		if (!isDefaultAvailable) {
			// Fallback priority: GOV -> PAS -> first available option
			const fallbackOptions = ["GOV", "PAS"];
			finalIdType =
				fallbackOptions.find((option) =>
					ID_TYPES.some((type) => type.value === option),
				) ||
				ID_TYPES[0]?.value ||
				"GOV";
		}

		// Only update if the current idType is different
		if (finalIdType && idType !== finalIdType) {
			setIdType(finalIdType);
		}
	}, [
		store.addressCountry,
		store.payoutCountry,
		hasUserSelectedIdType,
		ID_TYPES,
		idType,
	]);

	// Prefer the live dropdown selection over the stored value: when the store
	// already holds an issue country (returning profile / prefilled), reading it
	// first would discard a country the user just changed on this page. Shared by
	// handleSubmit and the Continue button's disabled state so they stay in sync.
	const currentIssueCountry = issueCountry || store.idIssueCountry;

	const handleSubmit = async () => {
		// Validate required fields - issue state is only required for certain countries
		let digitalProfileID = store.digitalProfileId || "";
		const isValidForm =
			idType &&
			idNumber &&
			currentIssueCountry &&
			(shouldShowIssueState(idType, currentIssueCountry) ? issueState : true);

		if (!isValidForm) {
			toast.validationError("Please fill in all required fields");
			return;
		}

		// Check if we have required transaction data (supports both v2 and v5 API)
		if (!store.transactionId || !authToken) {
			toast.error("Missing transaction information. Please start over.");
			return;
		}

		// Save identification data to store first
		rampsActions.setIdentificationData({
			idType,
			idNumber,
			idIssueCountry: currentIssueCountry,
			idIssueState: shouldShowIssueState(idType, currentIssueCountry)
				? issueState
				: "",
		});

		// Try to create profile if all data is available (backup safety net)
		// TanStack Store updates are synchronous, read fresh state directly
		if (
			isBankService(store.serviceOption) &&
			!store.digitalProfileId &&
			store.operation !== "deposit"
		) {
			digitalProfileID =
				(await createProfileIfNeeded({ requireAllFields: true })) || "";
		}

		// Navigate to the next appropriate page
		if (
			(isBankService(store.serviceOption) && digitalProfileID) ||
			!isBankService(store.serviceOption) ||
			store.operation === "deposit"
		) {
			handleContinue(search);
		}
	};

	const formatIdNumber = (value: string) => {
		// Allow alphanumeric characters, hyphens, asterisks, and spaces
		// Remove any characters that don't match the backend regex pattern
		return value.replace(/[^a-zA-Z0-9\-*\s]/g, "").toUpperCase();
	};

	const handleIdNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatIdNumber(e.target.value);
		setIdNumber(formatted);
	};

	const handleCountryChange = (value: string) => {
		setIssueCountry(value);
		setIssueState("");
		setHasUserSelectedIssueCountry(true);
	};

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label="Back"
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-8 px-4">
				<Progress
					value={80}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				<div className="mb-8 text-left">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Identification`}
					</RampsText>
				</div>

				{/* Form */}
				<div className="flex-1 space-y-6">
					<div className="space-y-4">
						{/* ID Type */}
						<RampsSelectBottomSheet
							label={t`ID type`}
							sheetTitle={t`Select ID type`}
							options={ID_TYPES.map((type) => ({
								value: type.value,
								label: type.label,
							}))}
							value={idType}
							onSelect={(value) => {
								setIdType(value);
								setHasUserSelectedIdType(true);
							}}
							size="compact"
							placeholder={t`Select ID type`}
						/>

						{/* ID Number */}
						<RampsInput
							id={idNumberFieldId}
							type="text"
							label={t`ID number`}
							value={idNumber}
							onChange={handleIdNumberChange}
						/>

						{/* ID Issue Country */}
						<RampsSelectBottomSheet
							label={t`ID issue country`}
							sheetTitle={t`Select country`}
							options={countryOptions.map((country) => ({
								value: country.value,
								label: country.label,
								countryCode: country.countryCode,
							}))}
							value={issueCountry}
							onSelect={(value) => handleCountryChange(value)}
							size="full"
							searchPlaceholder={t`Search countries...`}
							renderOptionIcon={(option) =>
								option.countryCode ? (
									<CircleFlag
										countryCode={option.countryCode}
										className="h-6 w-6"
									/>
								) : null
							}
							renderTriggerContent={(selectedOption) => (
								<div className="flex items-center gap-3">
									{selectedOption?.countryCode && (
										<CircleFlag
											countryCode={selectedOption.countryCode}
											className="h-6 w-6"
										/>
									)}
									<RampsText
										variant="body-default"
										color="primary"
										className="font-medium"
									>
										{selectedOption?.label}
									</RampsText>
								</div>
							)}
							placeholder={t`Select country`}
						/>

						{/* Issue State - Only show for state-issued documents in countries that have states */}
						{shouldShowIssueState(idType, issueCountry) && (
							<RampsSelectBottomSheet
								label={t`Issue state`}
								sheetTitle={
									issueCountry === "USA" ? t`Select state` : t`Select region`
								}
								options={getSubdivisions(issueCountry).map((state) => ({
									value: state.value,
									label: state.label,
								}))}
								value={issueState}
								onSelect={(value) => setIssueState(value)}
								size="full"
								searchPlaceholder={t`Search...`}
								placeholder={t`Select state`}
							/>
						)}

						{/* Information notice */}
						<div className="flex gap-3">
							<FormReminder
								text={t`You'll need to show this ID at the MoneyGram location`}
								icon={IdCard}
							/>
						</div>
					</div>
				</div>

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<RampsButton
						onClick={handleSubmit}
						disabled={
							!idType ||
							!idNumber ||
							!currentIssueCountry ||
							(shouldShowIssueState(idType, currentIssueCountry) &&
								!issueState) ||
							isCreating ||
							isAssociating
						}
						loading={isCreating || isAssociating}
						className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{t`Continue`}
					</RampsButton>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/identification")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: IdentificationPage,
});
